import React, { InputHTMLAttributes, forwardRef } from 'react';
import { clsx } from 'clsx';

interface NumberInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  prefix?: string;
  suffix?: string;
  onChange?: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
}

const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ 
    label, 
    error, 
    helperText, 
    size = 'md', 
    prefix,
    suffix,
    onChange,
    className,
    disabled,
    min,
    max,
    step = 1,
    precision,
    value,
    defaultValue,
    ...props 
  }, ref) => {
    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-3 py-2 text-sm',
      lg: 'px-4 py-3 text-base'
    };

    const baseClasses = 'block w-full rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed';
    
    const stateClasses = error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500';

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const stringValue = e.target.value;
      
      if (stringValue === '') {
        onChange?.(null);
        return;
      }

      let numValue = parseFloat(stringValue);
      
      // Apply precision without double conversion to avoid floating-point errors
      if (precision !== undefined && !isNaN(numValue)) {
        // Use Math.round with power of 10 to avoid parseFloat(toFixed()) precision issues
        const factor = Math.pow(10, precision);
        numValue = Math.round(numValue * factor) / factor;
      }
      
      // Apply min/max constraints
      if (min !== undefined && numValue < min) {
        numValue = min;
      }
      if (max !== undefined && numValue > max) {
        numValue = max;
      }
      
      onChange?.(isNaN(numValue) ? null : numValue);
    };

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
        )}
        
        <div className="relative">
          {prefix && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 text-sm">{prefix}</span>
            </div>
          )}
          
          <input
            ref={ref}
            type="number"
            step={step}
            min={min}
            max={max}
            value={value}
            defaultValue={defaultValue}
            className={clsx(
              baseClasses,
              stateClasses,
              sizeClasses[size],
              prefix && 'pl-8',
              suffix && 'pr-8',
              className
            )}
            disabled={disabled}
            onChange={handleChange}
            {...props}
          />
          
          {suffix && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span className="text-gray-500 text-sm">{suffix}</span>
            </div>
          )}
        </div>
        
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
        
        {helperText && !error && (
          <p className="mt-1 text-sm text-gray-500">{helperText}</p>
        )}
      </div>
    );
  }
);

NumberInput.displayName = 'NumberInput';

export default NumberInput; 